import React from 'react';
import { PatientHealthDataTableResponse } from '../../api/generated/models';
import { useTranslation } from '../../i18n';
import { buildSleepChartModel, getSleepSegments, SleepStageType } from './sleepChartModel';

export const SleepStagesChart = (props: {
  data?: PatientHealthDataTableResponse,
  width?: number,
  height?: number,
  debug?: boolean,
}) => {
  const { t } = useTranslation();
  const width = props.width ?? 240;
  const height = props.height ?? 180;
  const debug = !!props.debug;

  const svgIdRef = React.useRef<string>(`sleep_chart_${Math.random().toString(36).slice(2)}`);
  const svgId = svgIdRef.current;

  const segmentsRaw = React.useMemo(() => getSleepSegments(props.data), [props.data]);
  const hasRenderableSegments = React.useMemo(
    () => segmentsRaw.some((s) => s.type !== 'inBed'),
    [segmentsRaw],
  );

  const labelWidth = 56;
  const headerHeight = 18;
  const paddingRight = 8;
  const paddingBottom = 8;

  const plotWidth = Math.max(0, width - labelWidth - paddingRight);
  const plotHeight = Math.max(0, height - headerHeight - paddingBottom);

  const plotModel = buildSleepChartModel({ width: plotWidth, height: plotHeight, data: props.data });

  // eslint-disable-next-line i18next/no-literal-string
  const gradId = `${svgId}_grad`;
  // eslint-disable-next-line i18next/no-literal-string
  const maskId = `${svgId}_mask`;

  const getStageLabel = (type: SleepStageType) => {
    switch (type) {
      case 'awake':
        return t('Awake');
      case 'rem':
        return t('REM');
      case 'core':
        return t('Core');
      case 'deep':
        return t('Deep');
      case 'asleepUnspecified':
        return t('Asleep');
      case 'inBed':
        return t('In Bed');
      default:
        return '';
    }
  };

  React.useEffect(() => {
    if (!debug) {
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[SleepStagesChart] has_more_rows', props.data?.has_more_rows);
    // eslint-disable-next-line no-console
    console.log('[SleepStagesChart] raw rows', props.data?.rows ?? []);
    // eslint-disable-next-line no-console
    console.log('[SleepStagesChart] normalized segments', segmentsRaw);
    // eslint-disable-next-line no-console
    console.log('[SleepStagesChart] chart model', plotModel);
  }, [debug, props.data, segmentsRaw, plotModel]);

  if (!hasRenderableSegments) {
    return null;
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0.2" x2="0" y2="0.95">
          {plotModel.stages.map((stage) => {
            const offset = plotModel.stageCount > 1 ? stage.row / (plotModel.stageCount - 1) : 0;
            return (
              <stop
                key={stage.type}
                offset={`${offset * 100}%`}
                stopColor={stage.color}
                stopOpacity={0.3}
              />
            );
          })}
        </linearGradient>
        <mask id={maskId} maskUnits="userSpaceOnUse" x={0} y={0} width={plotWidth} height={plotHeight}>
          <rect x={0} y={0} width={plotWidth} height={plotHeight} fill="black" />
          {plotModel.bars.map((bar) => {
            const padX = 2;
            const padY = 1;
            return (
              <rect
                key={bar.id}
                x={Math.max(0, bar.x - padX)}
                y={Math.max(0, bar.y - padY)}
                width={Math.max(0, bar.width + padX * 2)}
                height={Math.max(0, bar.height + padY * 2)}
                rx={8}
                fill="white"
              />
            );
          })}
          {plotModel.connectors.map((c, idx) => (
            <line
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              x1={c.x}
              x2={c.x}
              y1={c.y1}
              y2={c.y2}
              stroke="white"
              strokeWidth={plotModel.borderWidth}
              strokeLinecap="round"
            />
          ))}
        </mask>
      </defs>

      <g transform={`translate(${labelWidth},${headerHeight})`}>
        {plotModel.stages.map((stage, idx) => {
          if (idx === 0) {
            return null;
          }
          const y = stage.row * plotModel.rowHeight;
          return (
            <line
              key={stage.type}
              x1={0}
              x2={plotWidth}
              y1={y}
              y2={y}
              stroke="#d5d5d5"
              strokeWidth={1}
            />
          );
        })}
        {plotModel.cols.map((col, idx) => {
          if (idx === plotModel.cols.length - 1) {
            return null;
          }
          return (
            <line
              key={idx}
              x1={col.x}
              x2={col.x}
              y1={0}
              y2={plotHeight}
              stroke="#d5d5d5"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          );
        })}

        <rect x={0} y={0} width={plotWidth} height={plotHeight} fill={`url(#${gradId})`} mask={`url(#${maskId})`} />

        {plotModel.bars.map((bar) => (
          <rect
            key={bar.id}
            x={bar.x}
            y={bar.y}
            width={Math.max(0, bar.width)}
            height={Math.max(0, bar.height)}
            rx={6}
            fill={bar.color}
          />
        ))}
      </g>

      {plotModel.stages.map((stage) => {
        const y = headerHeight + stage.row * plotModel.rowHeight + plotModel.rowHeight / 2;
        return (
          <text
            key={stage.type}
            x={6}
            y={y}
            fontSize={13}
            dominantBaseline="middle"
            fill="#555"
          >
            {getStageLabel(stage.type)}
          </text>
        );
      })}

      {plotModel.cols.map((col, idx) => (
        <text
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          x={labelWidth + col.x + 2}
          y={12}
          fontSize={9}
          fill="#666"
        >
          {col.label}
        </text>
      ))}
    </svg>
  );
};
